import { isPartnerWinery } from '@helpers/winery';
import {
  ALL_REVIEWS_ID,
  EDITOR_NOTE_ID,
  VINTAGE_LIST_ID,
  retry,
  scrollToElementByUrlHash,
} from '@vivino/js-web-common';
import React, { lazy, useEffect } from 'react';
import {
  CART_ITEM_SOURCE_TYPES,
  CartItemSourceContext,
} from 'vivino-js/context/CartItemSourceContext/CartItemSourceContext';
import { useUserContext } from 'vivino-js/context/UserContext';
import {
  AdPosition,
  Promotion,
  adsToBeShown,
  getExternalAdInfo,
} from 'vivino-js/helpers/adsHelper';
import { consoleLogVintage } from 'vivino-js/helpers/consoleHelpers';
import { HighlightTypes, getFilteredHighlights } from 'vivino-js/helpers/highlights';
import { getFilteredCriticReviewsByCountry } from 'vivino-js/helpers/reviews';
import t from 'vivino-js/translationString';
import DynamicModuleLoader from 'vivino-ui/atoms/DynamicModuleLoader';
import PageSection from 'vivino-ui/atoms/PageSection';

import MeetTheRegion from '@components/MeetTheRegion/MeetTheRegion';
import MeetTheWinery from '@components/MeetTheWinery/MeetTheWinery';
import WineryCard, {
  WineryCardTrackingEvents,
  WineryCardTrackingSources,
} from '@components/WineryCard/WineryCard';

import FullEditorNote from 'vivino-ui/components/EditorNote/FullEditorNote';
import ExternalAd from 'vivino-ui/components/ExternalAd';
import FoodPairing from 'vivino-ui/components/FoodPairing';
import { Grid } from 'vivino-ui/components/Grid';
import TasteCharacteristics from 'vivino-ui/components/TasteCharacteristics';
import WineFacts from 'vivino-ui/components/WineFacts';
import WineTasteStyle from 'vivino-ui/components/WineTasteStyle';

import CriticReviewList from '../CriticReviewList/CriticReviewList.presentational';

export const ALL_DEALS_ID = 'all_deals';

const TRANSLATIONS = {
  otherOffers: 'components.deal_wine_page.deal_wine_page_data_container.other_offers',
  lowInventoryMessage:
    'components.deal_wine_page.deal_wine_page_data_container.low_inventory_message',
  lowInventorySubMessage:
    'components.deal_wine_page.deal_wine_page_data_container.low_inventory_sub_message',
};

const HighlightList = lazy(() =>
  retry(
    () =>
      // @ts-ignore
      import(
        /* webpackChunkName: "HighlightList" */

        'vivino-ui/components/HighlightList'
      )
  )
);

const DealsCountdown = lazy(() =>
  retry(
    () =>
      // @ts-ignore
      import(
        /* webpackChunkName: "DealsCountdown" */

        '../DealsCountdown/DealsCountdown'
      )
  )
);

const WineCriticReviews = lazy(() =>
  retry(
    () =>
      // @ts-ignore
      import(
        /* webpackChunkName: "WineCriticReviews" */

        '../WineCriticReviews/WineCriticReviews'
      )
  )
);

const CommunityReviewsList = lazy(() =>
  retry(
    () =>
      // @ts-ignore
      import(
        /* webpackChunkName: "CommunityReviews" */

        'vivino-ui/components/CommunityReviews/components/CommunityReviewsList'
      )
  )
);

const WineCardList = lazy(() =>
  retry(
    () =>
      // @ts-ignore
      import(
        /* webpackChunkName: "WineCardList" */

        'vivino-ui/components/WineCardList'
      )
  )
);

const VintageList = lazy(() =>
  retry(
    () =>
      // @ts-ignore
      import(
        /* webpackChunkName: "VintageList" */

        'vivino-ui/components/VintageList'
      )
  )
);

interface OffersPageProps {
  offerPageInformation: any;
  countryCode: string;
  promotions: Promotion[];
}

const OffersPage = ({ offerPageInformation, countryCode, promotions }: OffersPageProps) => {
  const position = AdPosition.MIDDLE;
  const externalAdInfo = getExternalAdInfo({
    promotions,
    position,
  });

  const dataAdUnit = externalAdInfo?.ad_unit_id;
  const adDivId = externalAdInfo?.div_id;

  useEffect(scrollToElementByUrlHash, []);
  const vintage = offerPageInformation?.vintage;
  const vintageId = vintage.id;
  const vintageWine = vintage.wine;
  const winery = vintageWine?.winery;
  const price = offerPageInformation?.price;
  const merchant = offerPageInformation?.merchant;
  const tastes = offerPageInformation?.tastes;
  const deal = price?.deal;

  const criticReviews = getFilteredCriticReviewsByCountry({
    countryCode,
    reviews: deal?.critic_reviews,
  });
  const promoPageInformation = deal?.promo_page_information;

  const highlights = getFilteredHighlights({
    highlights: offerPageInformation?.highlights,
    filters: [HighlightTypes.PriceQpr],
  });

  consoleLogVintage({ vintage });

  const showVintageSection = (vintageWine.vintages || []).length >= 2;

  const { user } = useUserContext();

  const showAds = adsToBeShown({ promotions, user });

  return (
    <>
      <Grid>
        {winery && (
          <PageSection>
            <MeetTheWinery vintageId={vintage.id} winery={winery} />
          </PageSection>
        )}
        {vintageWine?.region?.id && (
          <PageSection>
            <MeetTheRegion regionId={vintageWine?.region?.id} vintageId={vintageWine?.id} />
          </PageSection>
        )}

        {vintage && (
          <PageSection>
            <WineFacts vintage={vintage} />
          </PageSection>
        )}

        {vintage && (
          <DynamicModuleLoader id={ALL_REVIEWS_ID}>
            <PageSection>
              <CommunityReviewsList />
            </PageSection>
          </DynamicModuleLoader>
        )}

        <PageSection>
          <CriticReviewList criticReviewList={criticReviews} />
        </PageSection>

        <DynamicModuleLoader>
          <PageSection>
            <DealsCountdown
              sellingPoints={promoPageInformation?.selling_points}
              offerEndDate={promoPageInformation?.offer_end_date}
              bottleCount={price?.inventory_bottles_count}
            />
          </PageSection>
        </DynamicModuleLoader>
        <div id={EDITOR_NOTE_ID}>
          <PageSection>
            <FullEditorNote
              offerTitle={promoPageInformation?.offer_title}
              editorNote={promoPageInformation?.editors_note}
            />
          </PageSection>
        </div>
        {highlights?.length > 0 && (
          <DynamicModuleLoader>
            <PageSection>
              <HighlightList highlights={highlights} />
            </PageSection>
          </DynamicModuleLoader>
        )}

        <DynamicModuleLoader>
          <PageSection>
            <WineCriticReviews criticReviews={criticReviews} />
          </PageSection>
        </DynamicModuleLoader>

        <PageSection>
          <TasteCharacteristics vintage={vintage} tastes={tastes} />
        </PageSection>

        <PageSection>
          <FoodPairing vintage={vintage} />
        </PageSection>

        {showAds && <ExternalAd divId={adDivId} dataAdSlot={dataAdUnit} />}
      </Grid>
      <PageSection>
        <WineTasteStyle styleData={vintageWine?.style} regionData={vintageWine?.region} />
      </PageSection>
      <Grid>
        {winery && !isPartnerWinery(winery) && (
          <PageSection>
            <WineryCard
              winery={winery}
              winesCTA={false}
              trackingSource={WineryCardTrackingSources.PDP}
              trackingEvents={{
                show: WineryCardTrackingEvents.PDPShow,
                learnMore: WineryCardTrackingEvents.PDPLearnMore,
              }}
            />
          </PageSection>
        )}

        {showVintageSection && (
          <DynamicModuleLoader id={VINTAGE_LIST_ID}>
            <PageSection>
              <VintageList
                vintages={vintageWine.vintages}
                wine={vintage.wine}
                highlights={highlights}
              />
            </PageSection>
          </DynamicModuleLoader>
        )}
        <div id={ALL_DEALS_ID}>
          <DynamicModuleLoader>
            <PageSection>
              <CartItemSourceContext.Provider value={CART_ITEM_SOURCE_TYPES.WINE_PAGE_OTHER_OFFERS}>
                <WineCardList
                  title={t(TRANSLATIONS.otherOffers)}
                  filteredVintageIds={[vintageId]}
                  amount={price?.amount}
                  wineTypeId={vintageWine?.type_id}
                  merchantId={merchant?.id}
                  currencyCode={price?.currency?.code}
                />
              </CartItemSourceContext.Provider>
            </PageSection>
          </DynamicModuleLoader>
        </div>
      </Grid>
    </>
  );
};

export default OffersPage;
